import React from 'react';
import { Edit, Trash2, FileText, MessageCircle, Eye, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

const ReservaTable = ({
  reservas,
  canales,
  estadosReserva,
  onVerDetalle,
  onEditar,
  onEliminar,
  onCrearContrato,
  onEnviarWhatsApp
}) => {
  const formatCurrency = (value) => {
    return `$${Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T12:00:00');
    return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const obtenerColorEstado = (estado) => {
    const estadoConfig = estadosReserva.find(e => e.nombre === estado);
    if (estadoConfig) {
      return {
        fondo: estadoConfig.color,
        texto: estadoConfig.color_texto || '#FFFFFF'
      };
    }
    return { fondo: '#8FE0FF', texto: '#0E3A53' };
  };

  const obtenerNombreCanal = (canalId) => {
    const canal = canales.find(c => c.id_canal === canalId);
    return canal?.nombre || 'Sin canal';
  };

  const obtenerColorCanal = (canalId) => {
    const canal = canales.find(c => c.id_canal === canalId);
    return canal?.color || '#8FE0FF';
  };

  if (reservas.length === 0) {
    return (
      <div className="text-center py-12 text-[#0E3A53]/60">
        <p className="text-lg">No hay reservas que mostrar</p>
        <p className="text-sm mt-2">Crea tu primera reserva con el botón "Nueva Reserva"</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid="reservas-table">
        <thead>
          <tr className="border-b-2 border-[#8FE0FF]">
            <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">Check-in</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">Check-out</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">Huésped</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">Canal</th>
            <th className="px-4 py-3 text-left text-sm font-semibold text-[#0E3A53]">Noches</th>
            <th className="px-4 py-3 text-right text-sm font-semibold text-[#0E3A53]">Monto Neto</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-[#0E3A53]">Estado</th>
            <th className="px-4 py-3 text-center text-sm font-semibold text-[#0E3A53]">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {reservas.map((reserva) => {
            const coloresEstado = obtenerColorEstado(reserva.estado_reserva);
            const montoNeto = reserva.monto_bruto - reserva.comisiones;
            const esRecurrente = reserva.gasto_recurrente_id;
            
            return (
              <tr 
                key={reserva.id_reserva} 
                className="border-b border-[#8FE0FF]/30 hover:bg-[#8FE0FF]/10 transition-colors cursor-pointer"
                onClick={() => onVerDetalle(reserva)}
                data-testid={`reserva-row-${reserva.id_reserva}`}
              >
                <td className="px-4 py-3 text-sm text-[#0E3A53]">
                  <div className="flex items-center gap-2">
                    {esRecurrente && (
                      <RefreshCw className="w-3 h-3 text-[#1AA1D8]" title="Gasto recurrente" />
                    )}
                    {formatDate(reserva.fecha_check_in)}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-[#0E3A53]">
                  {formatDate(reserva.fecha_check_out)}
                </td>
                <td className="px-4 py-3 text-sm font-medium text-[#0E3A53]">
                  {reserva.huesped_nombre}
                </td>
                <td className="px-4 py-3">
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-medium text-white"
                    style={{ backgroundColor: obtenerColorCanal(reserva.canal_id) }}
                  >
                    {obtenerNombreCanal(reserva.canal_id)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-[#0E3A53] text-center">
                  {reserva.noches}
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-[#1AA1D8] text-right">
                  {formatCurrency(montoNeto)}
                </td>
                <td className="px-4 py-3 text-center">
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: coloresEstado.fondo,
                      color: coloresEstado.texto
                    }}
                  >
                    {reserva.estado_reserva}
                  </span>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onVerDetalle(reserva)}
                      className="text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                      title="Ver detalle"
                      data-testid={`btn-detail-${reserva.id_reserva}`}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEnviarWhatsApp(reserva)}
                      className="text-[#25D366] hover:bg-[#25D366]/10"
                      title="Enviar WhatsApp"
                      data-testid={`btn-whatsapp-${reserva.id_reserva}`}
                    >
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onCrearContrato(reserva)}
                      className="text-[#0E3A53] hover:bg-[#F7E8C9]/30"
                      title="Crear contrato"
                      data-testid={`btn-contract-${reserva.id_reserva}`}
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEditar(reserva)}
                      className="text-[#1AA1D8] hover:bg-[#8FE0FF]/20"
                      title="Editar"
                      data-testid={`btn-edit-${reserva.id_reserva}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEliminar(reserva)}
                      className="text-[#FF7F6A] hover:bg-[#FF7F6A]/10"
                      title="Eliminar"
                      data-testid={`btn-delete-${reserva.id_reserva}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ReservaTable;
